import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { fetchOfficerTasks, type OfficerTasksData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MetricGrid, MobileSpinner, EmptyMobile, SeverityPill,
} from "../../../liff/components/Mobile.js";

export function OfficerTasksPlaceholderPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<OfficerTasksData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchOfficerTasks(session.tenant_id, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session]);

  if (error) return (
    <MobileShell title="無法載入任務"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;

  const remaining = data.tasks_today.filter((t) => t.status !== "done").length;
  const overdue = data.tasks_today.filter((t) => t.status === "overdue").length;

  return (
    <MobileShell title={data.display_name ?? "我的任務"} subtitle={`${data.date_local} · ${data.shift_label}`}>
      <MetricGrid items={[
        { label: "今日任務", value: String(data.tasks_today.length), tone: "default" },
        { label: "未完成", value: String(remaining), tone: remaining > 0 ? "warning" : "success" },
        { label: "我的漏巡", value: String(data.self_overdue_summary.count_today), tone: data.self_overdue_summary.count_today > 0 ? "danger" : "success" },
        { label: "最長逾時", value: `${data.self_overdue_summary.longest_overdue_minutes} 分`, tone: data.self_overdue_summary.longest_overdue_minutes > 10 ? "danger" : "default" },
      ]} />

      <Card>
        <CardHeader title="今日任務" hint={`${data.tasks_today.filter((t) => t.status === "done").length}/${data.tasks_today.length}`} />
        <ol className="divide-y divide-border-subtle -my-2">
          {data.tasks_today.map((t) => (
            <li key={t.id} className="py-2 flex items-center gap-2">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                t.status === "done" ? "bg-success" : t.status === "overdue" ? "bg-danger" : "bg-warning"
              }`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm truncate">{t.location_label}</p>
                <p className="text-[11px] text-text-muted truncate">{t.site_name} · {t.expected_at_local}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider shrink-0 text-text-muted">
                {t.status === "done" ? "完成" : t.status === "overdue" ? "已逾時" : "未到"}
              </span>
            </li>
          ))}
        </ol>
      </Card>

      {data.pending_locations.length > 0 && (
        <Card>
          <CardHeader title="未完成點位" hint={`${data.pending_locations.length} 點`} />
          <ul className="divide-y divide-border-subtle -my-2">
            {data.pending_locations.map((p) => (
              <li key={p.id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm truncate">{p.location_label}</p>
                  <p className="text-[11px] text-text-muted">{p.site_name}</p>
                </div>
                <span className="text-xs text-danger shrink-0">已逾 {p.minutes_overdue} 分</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <Card>
        <CardHeader title="最近警報" hint={`${data.recent_alerts.length} 筆`} />
        {data.recent_alerts.length === 0 ? <EmptyMobile title="近期沒有警報 🎉" /> : (
          <ul className="space-y-2">
            {data.recent_alerts.map((a) => (
              <li key={a.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium truncate">{labelForKind(a.kind)}</p>
                  <SeverityPill severity={a.severity} />
                </div>
                <p className="text-[11px] text-text-muted mt-1">{a.site_name} · {a.at_local}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {overdue > 0 && (
        <Card className="border-danger/40">
          <p className="text-xs text-danger font-medium">你今天有 {overdue} 個任務已逾時。請優先補完並回報。</p>
        </Card>
      )}
    </MobileShell>
  );
}

function labelForKind(k: OfficerTasksData["recent_alerts"][number]["kind"]): string {
  return k === "overdue" ? "巡邏逾時" : k === "incident" ? "事件警報" : "系統風險";
}
